import { useState, useEffect, useMemo } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../contexts/AuthContext";

/**
 * Loads `job_listing_purchases` for the signed-in user as `employer_id`.
 */
export default function usePaymentHistory() {
    const { user } = useAuth();
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!user?.id) {
            setPaymentHistory([]);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;

        const load = async () => {
            try {
                setLoading(true);
                setError(null);

                const { data, error: qError } = await supabase
                    .from("job_listing_purchases")
                    .select("*")
                    .eq("employer_id", user.id)
                    .order("created_at", { ascending: false });

                if (cancelled) return;
                if (qError) throw qError;

                const rows = data || [];
                const mapped = rows.map((row) => ({
                    ...row,
                    amount: Number(row.amount_paid),
                    /** Alias for UI that expects `raw_response` (Paystack / metadata). */
                    raw_response: row.paystack_payload,
                }));

                setPaymentHistory(mapped);
            } catch (e) {
                if (!cancelled) {
                    setPaymentHistory([]);
                    setError(
                        e.message || "Failed to load payment history."
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [user?.id]);

    const isEmpty = useMemo(
        () => !loading && paymentHistory.length === 0,
        [loading, paymentHistory.length]
    );

    return { paymentHistory, loading, error, isEmpty };
}
